import { useEffect, useState } from "react";
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Calendar as CalendarIcon,
  Clock,
  Users,
  MapPin,
  Plus,
  Edit2,
  Trash2,
  ArrowLeft,
  UserPlus,
  Mail
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { EmployeeSelector } from "@/components/training/EmployeeSelector";

interface TrainingSession {
  id: string;
  title: string;
  description: string;
  instructor: string;
  location: string;
  start_date: string;
  end_date: string;
  status: string;
  created_at: string;
}

interface TrainingTopic {
  id: string;
  title_en: string;
  title_el: string;
}

const ScheduledSessions = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const { user } = useSupabaseUser();
  const { isAdmin, isLoading: isLoadingRoles } = useUserRoles();
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [topics, setTopics] = useState<TrainingTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSession, setEditingSession] = useState<TrainingSession | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [showEmployeeSelector, setShowEmployeeSelector] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [sessionParticipants, setSessionParticipants] = useState<Record<string, string[]>>({});

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructor: '',
    location: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    // Only fetch data if user roles are loaded
    if (!isLoadingRoles && user) {
      fetchData();
    }
  }, [user, isLoadingRoles]);

  // Set up real-time listeners for training sessions and participants
  useEffect(() => {
    // Listen for changes in training sessions
    const trainingSessionsChannel = supabase
      .channel(`training_sessions_realtime_${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'training_sessions'
        },
        (payload) => {
          console.log('Training session changed:', payload);
          fetchData(); // Refetch all data to ensure consistency
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'training_session_participants'
        },
        (payload) => {
          console.log('Training session participants changed:', payload);
          fetchData(); // Refetch all data to ensure consistency
        }
      )
      .subscribe();

    // Listen for changes in team schedules that might affect training shifts
    const teamSchedulesChannel = supabase
      .channel(`team_schedules_training_sync_${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'team_schedules',
          filter: 'color=eq.#10b981' // Only listen to training shifts
        },
        (payload) => {
          console.log('Training shift changed in team schedule:', payload);
          // Invalidate schedules query to update scheduling views
          queryClient.invalidateQueries({ queryKey: ['schedules'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(trainingSessionsChannel);
      supabase.removeChannel(teamSchedulesChannel);
    };
  }, [queryClient]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch training sessions from the database
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('training_sessions')
        .select('*')
        .order('start_date', { ascending: true });

      if (sessionsError) throw sessionsError;
      setSessions(sessionsData || []);

      // Fetch training topics for dropdown
      const { data: topicsData, error: topicsError } = await supabase
        .from('training_topics')
        .select('id, title_en, title_el')
        .eq('is_active', true);

      if (topicsError) throw topicsError;
      setTopics(topicsData || []);

      // Fetch session participants
      const { data: participantsData, error: participantsError } = await supabase
        .from('training_session_participants')
        .select('session_id, user_id');

      if (participantsError) throw participantsError;
      
      // Group participants by session
      const participantsBySession: Record<string, string[]> = {};
      participantsData?.forEach(participant => {
        if (!participantsBySession[participant.session_id]) {
          participantsBySession[participant.session_id] = [];
        }
        participantsBySession[participant.session_id].push(participant.user_id);
      });
      setSessionParticipants(participantsBySession);

    } catch (error) {
      console.error('Error fetching session data:', error);
      toast.error('Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate that dates are properly formatted
      if (!formData.start_date || !formData.end_date) {
        toast.error('Please select both start and end dates');
        return;
      }

      // Convert datetime-local format to ISO string for database storage
      const startDateISO = fromLocalISOString(formData.start_date).toISOString();
      const endDateISO = fromLocalISOString(formData.end_date).toISOString();

      // Validate that end date is after start date
      if (new Date(endDateISO) <= new Date(startDateISO)) {
        toast.error('End date must be after start date');
        return;
      }

      let sessionId = editingSession?.id;
      
      if (editingSession) {
        // Update existing session
        const { error } = await supabase
          .from('training_sessions')
          .update({
            title: formData.title,
            description: formData.description,
            instructor: formData.instructor,
            location: formData.location,
            start_date: startDateISO,
            end_date: endDateISO,
            status: 'scheduled'
          })
          .eq('id', editingSession.id);

        if (error) throw error;
        toast.success('Session updated successfully');
      } else {
        // Create new session
        const { data: newSession, error } = await supabase
          .from('training_sessions')
          .insert({
            description: formData.title, // Using description for title temporarily
            instructor: formData.instructor,
            location: formData.location,
            start_date: startDateISO,
            end_date: endDateISO,
            scheduled_date: startDateISO,
            status: 'scheduled'
          } as any)
          .select()
          .single();

        if (error) throw error;
        sessionId = newSession.id;
        toast.success('Session scheduled successfully');
      }

      // Handle participants if any are selected
      if (selectedEmployees.length > 0 && sessionId) {
        await saveSessionParticipants(sessionId, selectedEmployees);
      }
      
      setShowDialog(false);
      setEditingSession(null);
      setSelectedEmployees([]);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving session:', error);
      toast.error('Failed to save session');
    }
  };

  const getBestTeamForEmployee = async (employeeId: string) => {
    try {
      // Get current week's shifts for this employee across all teams
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1); // Monday
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6); // Sunday

      // First try to find shifts for current week
      const { data: currentWeekShifts, error: weekError } = await supabase
        .from('team_schedules')
        .select('team_id, assigned_guards')
        .contains('assigned_guards', [employeeId])
        .gte('start_date', startOfWeek.toISOString())
        .lte('start_date', endOfWeek.toISOString())
        .neq('color', '#10b981'); // Exclude training shifts from count

      if (weekError) throw weekError;

      // Count shifts per team for current week
      const teamShiftCounts: { [key: string]: number } = {};
      currentWeekShifts?.forEach(shift => {
        teamShiftCounts[shift.team_id] = (teamShiftCounts[shift.team_id] || 0) + 1;
      });

      // Find team with most shifts this week
      let bestTeamId = '';
      let maxShifts = 0;
      
      Object.entries(teamShiftCounts).forEach(([teamId, count]) => {
        if (count > maxShifts) {
          maxShifts = count;
          bestTeamId = teamId;
        }
      });

      // If no shifts found this week, look at past 30 days
      if (!bestTeamId) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data: recentShifts, error: recentError } = await supabase
          .from('team_schedules')
          .select('team_id, assigned_guards')
          .contains('assigned_guards', [employeeId])
          .gte('start_date', thirtyDaysAgo.toISOString())
          .neq('color', '#10b981'); // Exclude training shifts

        if (recentError) throw recentError;

        const recentTeamCounts: { [key: string]: number } = {};
        recentShifts?.forEach(shift => {
          recentTeamCounts[shift.team_id] = (recentTeamCounts[shift.team_id] || 0) + 1;
        });

        let maxRecentShifts = 0;
        Object.entries(recentTeamCounts).forEach(([teamId, count]) => {
          if (count > maxRecentShifts) {
            maxRecentShifts = count;
            bestTeamId = teamId;
          }
        });
      }

      // If still no team found, get first team membership
      if (!bestTeamId) {
        const { data: teamMember } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', employeeId)
          .limit(1)
          .maybeSingle();
        
        bestTeamId = teamMember?.team_id || '';
      }

      return bestTeamId;
    } catch (error) {
      console.error('Error getting best team for employee:', error);
      return null;
    }
  };

  const createTrainingShifts = async (sessionId: string, sessionTitle: string, employeeIds: string[], startDate: string, endDate: string) => {
    try {
      console.log('Creating training shifts for:', { sessionId, sessionTitle, employeeIds, startDate, endDate });
      
      // Group employees by their best team
      const employeeTeamMap: { [teamId: string]: string[] } = {};
      
      for (const employeeId of employeeIds) {
        console.log('Getting best team for employee:', employeeId);
        const bestTeamId = await getBestTeamForEmployee(employeeId);
        console.log('Best team for employee', employeeId, ':', bestTeamId);
        
        if (bestTeamId) {
          if (!employeeTeamMap[bestTeamId]) {
            employeeTeamMap[bestTeamId] = [];
          }
          employeeTeamMap[bestTeamId].push(employeeId);
        } else {
          console.warn('No team found for employee:', employeeId);
        }
      }

      console.log('Employee team mapping:', employeeTeamMap);

      // Create one shift per team with all employees assigned to that team
      const { data: user } = await supabase.auth.getUser();
      console.log('Current user for creating shifts:', user.user?.id);
      
      const shifts = Object.entries(employeeTeamMap).map(([teamId, teamEmployeeIds]) => ({
        team_id: teamId,
        title: sessionTitle,
        description: `TRAINING_SESSION:${sessionId}`, // Unique identifier for training shifts
        start_date: startDate,
        end_date: endDate,
        assigned_guards: teamEmployeeIds,
        created_by: user.user?.id,
        color: '#10b981', // Green color to distinguish training shifts
        location: '' // Training sessions don't need location in shifts
      }));

      console.log('Shifts to create:', shifts);

      if (shifts.length > 0) {
        console.log('Inserting shifts into team_schedules...');
        const { error } = await supabase
          .from('team_schedules')
          .insert(shifts);

        if (error) {
          console.error('Supabase error inserting shifts:', error);
          throw error;
        }
        
        console.log(`Created ${shifts.length} training shifts for session: ${sessionTitle}`);
        toast.success(`Created training shifts for ${Object.keys(employeeTeamMap).length} team(s)`);
        
        // Trigger real-time update for schedules
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
      } else {
        console.warn('No shifts to create - no employees assigned to teams');
        toast.error('No shifts created - employees may not be assigned to teams');
      }
    } catch (error) {
      console.error('Error creating training shifts:', error);
      toast.error(`Failed to create training shifts: ${error.message || error}`);
    }
  };

  const removeTrainingShifts = async (sessionId: string, sessionTitle?: string) => {
    try {
      console.log('Removing training shifts for session:', sessionId);
      
      // Find training shifts by session ID in description or by session title if provided
      let query = supabase
        .from('team_schedules')
        .select('id, title, description')
        .eq('color', '#10b981'); // Match training shift color
      
      if (sessionId) {
        // Primary method: find by session ID in description
        query = query.like('description', `TRAINING_SESSION:${sessionId}%`);
      } else if (sessionTitle) {
        // Fallback method: find by old title matching
        query = query.eq('description', sessionTitle);
      }
      
      const { data: existingShifts, error: findError } = await query;
      
      if (findError) {
        console.error('Error finding training shifts:', findError);
        throw findError;
      }
      
      console.log('Found training shifts to remove:', existingShifts);
      
      if (existingShifts && existingShifts.length > 0) {
        const shiftIds = existingShifts.map(shift => shift.id);
        
        // Delete the found shifts
        const { error: deleteError } = await supabase
          .from('team_schedules')
          .delete()
          .in('id', shiftIds);
        
        if (deleteError) throw deleteError;
        
        console.log(`Successfully removed ${shiftIds.length} training shifts`);
        toast.success(`Removed ${shiftIds.length} associated training shift(s)`);
        
        // Invalidate schedules query to update the UI immediately
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
      } else {
        console.log('No training shifts found to remove');
      }
    } catch (error) {
      console.error('Error removing training shifts:', error);
      toast.error('Failed to remove training shifts');
    }
  };

  const saveSessionParticipants = async (sessionId: string, employeeIds: string[]) => {
    try {
      // Get current session data for shift management
      const { data: sessionData } = await supabase
        .from('training_sessions')
        .select('title, start_date, end_date')
        .eq('id', sessionId)
        .single();

      if (!sessionData) throw new Error('Session not found');

      // Get existing participants to handle removals
      const { data: existingParticipants } = await supabase
        .from('training_session_participants')
        .select('user_id')
        .eq('session_id', sessionId);

      const existingEmployeeIds = existingParticipants?.map(p => p.user_id) || [];

      // If this is an update, remove ALL existing training shifts for this session first
      if (existingEmployeeIds.length > 0) {
        await removeTrainingShifts(sessionId, sessionData.title);
      }

      // Remove existing participants
      await supabase
        .from('training_session_participants')
        .delete()
        .eq('session_id', sessionId);

      // Add new participants
      if (employeeIds.length > 0) {
        const participants = employeeIds.map(userId => ({
          session_id: sessionId,
          user_id: userId
        }));

        const { error } = await supabase
          .from('training_session_participants')
          .insert(participants);

        if (error) throw error;

        // Create new training shifts for all participants
        await createTrainingShifts(
          sessionId,
          sessionData.title,
          employeeIds,
          sessionData.start_date,
          sessionData.end_date
        );
      }
    } catch (error) {
      console.error('Error saving participants:', error);
      toast.error('Failed to save participants');
    }
  };

  const handleEdit = async (session: TrainingSession) => {
    setEditingSession(session);
    
    // Format dates for datetime-local input
    const formatForDateTimeLocal = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setFormData({
      title: session.title,
      description: session.description,
      instructor: session.instructor,
      location: session.location,
      start_date: formatForDateTimeLocal(session.start_date),
      end_date: formatForDateTimeLocal(session.end_date)
    });
    
    // Load existing participants for this session
    try {
      const { data: participantsData, error } = await supabase
        .from('training_session_participants')
        .select('user_id')
        .eq('session_id', session.id);
      
      if (error) throw error;
      
      const participantIds = participantsData?.map(p => p.user_id) || [];
      setSelectedEmployees(participantIds);
    } catch (error) {
      console.error('Error loading session participants:', error);
      // Fallback to cached data
      const participants = sessionParticipants[session.id] || [];
      setSelectedEmployees(participants);
    }
    
    setShowDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(language === 'en' ? 'Are you sure you want to delete this session?' : 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή τη συνεδρία;')) {
      return;
    }

    try {
      // Get session details before deletion to remove associated shifts
      const { data: sessionData } = await supabase
        .from('training_sessions')
        .select('title')
        .eq('id', id)
        .single();

      if (sessionData) {
        // Remove associated training shifts
        await removeTrainingShifts(id, sessionData.title);
      }

      const { error } = await supabase
        .from('training_sessions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Session deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete session');
    }
  };

  const handleNotifyParticipants = async (session: TrainingSession) => {
    try {
      const participants = sessionParticipants[session.id] || [];
      if (participants.length === 0) {
        toast.error(language === 'en' ? 'No participants found for this session' : 'Δεν βρέθηκαν συμμετέχοντες για αυτή τη συνεδρία');
        return;
      }

      // Get participant details including emails
      const { data: participantProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', participants);

      if (profilesError) throw profilesError;

      if (!participantProfiles || participantProfiles.length === 0) {
        toast.error(language === 'en' ? 'No participant details found' : 'Δεν βρέθηκαν στοιχεία συμμετεχόντων');
        return;
      }

      // Call the notification edge function
      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          type: 'training_session_notification',
          sessionData: {
            id: session.id,
            title: session.title,
            description: session.description,
            instructor: session.instructor,
            location: session.location,
            start_date: session.start_date,
            end_date: session.end_date,
            status: session.status
          },
          participants: participantProfiles,
          language: language
        }
      });

      if (error) throw error;

      toast.success(
        language === 'en' 
          ? `Notifications sent to ${participantProfiles.length} participant(s)` 
          : `Οι ειδοποιήσεις στάλθηκαν σε ${participantProfiles.length} συμμετέχοντα(ες)`
      );
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error(
        language === 'en' 
          ? 'Failed to send notifications' 
          : 'Αποτυχία αποστολής ειδοποιήσεων'
      );
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      instructor: '',
      location: '',
      start_date: '',
      end_date: ''
    });
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      scheduled: { label: language === 'en' ? 'Scheduled' : 'Προγραμματισμένο', variant: 'default' as const },
      ongoing: { label: language === 'en' ? 'Ongoing' : 'Σε εξέλιξη', variant: 'secondary' as const },
      completed: { label: language === 'en' ? 'Completed' : 'Ολοκληρώθηκε', variant: 'outline' as const },
      cancelled: { label: language === 'en' ? 'Cancelled' : 'Ακυρώθηκε', variant: 'destructive' as const }
    };
    
    const statusInfo = variants[status as keyof typeof variants] || variants.scheduled;
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const displaySessions = sessions;

  if (loading || isLoadingRoles) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden">
      {/* Enhanced background patterns */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/3 via-transparent to-blue-500/3 pointer-events-none"></div>
      <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-radial from-blue-500/8 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-radial from-primary/8 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: '2s' }}></div>
      
      <div className="relative z-10 container mx-auto p-6 space-y-8 animate-fade-in">
        {/* Enhanced Header Section */}
        <div className="bg-gradient-to-r from-card/80 via-card/60 to-card/40 backdrop-blur-sm rounded-2xl border border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                <CalendarIcon className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  {language === 'en' ? 'Scheduled Training Sessions' : 'Προγραμματισμένες Εκπαιδευτικές Συνεδρίες'}
                </h1>
                <p className="text-muted-foreground text-lg mt-2">
                  {language === 'en' ? 'Book and manage training sessions' : 'Κλείστε και διαχειριστείτε εκπαιδευτικές συνεδρίες'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/training')} className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'en' ? 'Back' : 'Πίσω'}
              </Button>
              {isAdmin && (
                <Dialog open={showDialog} onOpenChange={setShowDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { resetForm(); setEditingSession(null); }} className="hover-scale bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-md hover:shadow-lg">
                      <Plus className="h-4 w-4 mr-2" />
                      {language === 'en' ? 'Schedule Session' : 'Προγραμματισμός Συνεδρίας'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>
                        {editingSession 
                          ? (language === 'en' ? 'Edit Session' : 'Επεξεργασία Συνεδρίας')
                          : (language === 'en' ? 'Schedule New Session' : 'Προγραμματισμός Νέας Συνεδρίας')
                        }
                      </DialogTitle>
                      <DialogDescription>
                        {language === 'en' 
                          ? 'Fill in the details below to schedule a training session.'
                          : 'Συμπληρώστε τα στοιχεία παρακάτω για να προγραμματίσετε μια εκπαιδευτική συνεδρία.'
                        }
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="title">Title</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                          placeholder={language === 'en' ? 'Session title' : 'Τίτλος συνεδρίας'}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="description">Description</Label>
                        <Input
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder={language === 'en' ? 'Session description' : 'Περιγραφή συνεδρίας'}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="instructor">Instructor</Label>
                          <Input
                            id="instructor"
                            value={formData.instructor}
                            onChange={(e) => setFormData(prev => ({ ...prev, instructor: e.target.value }))}
                            placeholder={language === 'en' ? 'Instructor name' : 'Όνομα εκπαιδευτή'}
                          />
                        </div>
                        <div>
                          <Label htmlFor="location">Location</Label>
                          <Input
                            id="location"
                            value={formData.location}
                            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                            placeholder={language === 'en' ? 'Training location' : 'Τοποθεσία εκπαίδευσης'}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="start_date">Start Date & Time</Label>
                          <Input
                            id="start_date"
                            type="datetime-local"
                            value={formData.start_date}
                            onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label htmlFor="end_date">End Date & Time</Label>
                          <Input
                            id="end_date"
                            type="datetime-local"
                            value={formData.end_date}
                            onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                          />
                        </div>
                      </div>
                      
                      
                      {/* Add Employees Section */}
                      {formData.start_date && formData.end_date && (
                        <div className="border-t pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <Label>{language === 'en' ? 'Assigned Employees' : 'Ανατεθημένοι Υπάλληλοι'}</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowEmployeeSelector(true)}
                            >
                              <UserPlus className="h-4 w-4 mr-2" />
                              {language === 'en' ? 'Add Employees' : 'Προσθήκη Υπαλλήλων'}
                            </Button>
                          </div>
                          
                          {selectedEmployees.length > 0 && (
                            <div className="text-sm text-muted-foreground">
                              {language === 'en' 
                                ? `${selectedEmployees.length} employee(s) selected` 
                                : `${selectedEmployees.length} υπάλληλος(οι) επιλεγμένος(οι)`
                              }
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => setShowDialog(false)}>
                          {language === 'en' ? 'Cancel' : 'Ακύρωση'}
                        </Button>
                        <Button onClick={handleSubmit}>
                          {editingSession 
                            ? (language === 'en' ? 'Update' : 'Ενημέρωση')
                            : (language === 'en' ? 'Schedule' : 'Προγραμματισμός')
                          }
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>

        {/* Employee Selector Dialog */}
        <EmployeeSelector
          isOpen={showEmployeeSelector}
          onClose={() => setShowEmployeeSelector(false)}
          sessionStart={formData.start_date}
          sessionEnd={formData.end_date}
          onEmployeesSelected={setSelectedEmployees}
          selectedEmployees={selectedEmployees}
        />

        {/* Enhanced Sessions Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Calendar Card */}
          <Card className="lg:col-span-1 bg-gradient-to-br from-card via-card/90 to-blue-50/30 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <CalendarIcon className="h-4 w-4 text-white" />
                </div>
                {language === 'en' ? 'Calendar' : 'Ημερολόγιο'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                  className="rounded-md border bg-background w-full pointer-events-auto"
                />
              </div>
            </CardContent>
          </Card>

          {/* Sessions List */}
          <Card className="lg:col-span-3 bg-gradient-to-br from-card via-card/90 to-muted/30 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                  <Users className="h-4 w-4 text-primary-foreground" />
                </div>
                <div>
                  <CardTitle>
                    {language === 'en' ? 'Upcoming Sessions' : 'Επερχόμενες Συνεδρίες'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'en' ? 'View and manage scheduled training sessions' : 'Προβολή και διαχείριση προγραμματισμένων εκπαιδευτικών συνεδριών'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {displaySessions.map((session, index) => (
                  <div 
                    key={session.id} 
                    className="bg-gradient-to-r from-background/80 via-background/60 to-background/40 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 hover-scale group animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center group-hover:from-primary/30 group-hover:to-primary/20 transition-all">
                            <CalendarIcon className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">{session.title}</h3>
                            {getStatusBadge(session.status)}
                          </div>
                        </div>
                        <p className="text-muted-foreground mb-4 leading-relaxed">{session.description}</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div className="flex items-center space-x-2 p-2 bg-muted/50 rounded-lg">
                            <Clock className="h-4 w-4 text-primary" />
                            <span className="font-medium">{format(fromLocalISOString(session.start_date), 'MMM dd, yyyy HH:mm')}</span>
                          </div>
                          <div className="flex items-center space-x-2 p-2 bg-muted/50 rounded-lg">
                            <Users className="h-4 w-4 text-primary" />
                            <span className="font-medium">{session.instructor}</span>
                          </div>
                          <div className="flex items-center space-x-2 p-2 bg-muted/50 rounded-lg">
                            <MapPin className="h-4 w-4 text-primary" />
                            <span className="font-medium">{session.location}</span>
                          </div>
                        </div>
                      </div>
                      
                      {isAdmin && (
                        <div className="flex items-center space-x-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleNotifyParticipants(session)}
                            title={language === 'en' ? 'Notify Participants' : 'Ενημέρωση Συμμετεχόντων'}
                            className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(session)}
                            className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(session.id)}
                            className="hover-scale border-destructive/20 hover:border-destructive/40 hover:bg-destructive/5"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/training/sessions/${session.id}/participants`)}
                            title={language === 'en' ? 'Manage Participants' : 'Διαχείριση Συμμετεχόντων'}
                            className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5"
                          >
                            <Users className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {displaySessions.length === 0 && (
                  <div className="text-center py-16">
                    <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-muted to-muted/70 flex items-center justify-center mb-6">
                      <CalendarIcon className="h-10 w-10 text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      {language === 'en' ? 'No sessions scheduled' : 'Δεν υπάρχουν προγραμματισμένες συνεδρίες'}
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      {language === 'en' ? 'Get started by scheduling your first training session.' : 'Ξεκινήστε προγραμματίζοντας την πρώτη σας εκπαιδευτική συνεδρία.'}
                    </p>
                    {isAdmin && (
                      <Button onClick={() => { resetForm(); setEditingSession(null); setShowDialog(true); }} className="hover-scale bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-md hover:shadow-lg">
                        <Plus className="h-4 w-4 mr-2" />
                        {language === 'en' ? 'Schedule Your First Session' : 'Προγραμματίστε την Πρώτη Συνεδρία'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ScheduledSessions;
